/**
 * Live state of the builder for one bot: the open builder conversation (several per
 * bot, each with its own transcript), the run state and the event stream (resumable
 * by stream id), so leaving the page and coming back continues exactly where it was.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { PLAN_LIMIT_EVENT, streamEvents, studioApi, studioUrl } from './studioApi';

export const PENDING_KINDS = ['questions', 'secret', 'connect', 'confirm'];

function upsert(items, item) {
  const i = items.findIndex((x) => x.id === item.id);
  if (i === -1) return [...items, item];
  const copy = items.slice();
  copy[i] = item;
  return copy;
}

export default function useAgentBuilder(botId, handlers) {
  const [state, setState] = useState(null);
  const seq = useRef('');
  const conv = useRef('');
  const h = useRef(handlers);
  h.current = handlers;
  const reload = useRef(() => {});

  useEffect(() => {
    if (!botId) return undefined;
    let closed = false;
    let controller = null;
    let timer = null;

    const handle = (ev) => {
      if (ev.seq) seq.current = ev.seq;
      switch (ev.type) {
        case 'item': {
          const itemConv = ev.item.conversation_id || 'main';
          if (itemConv !== conv.current) {
            // activity in another builder conversation: only refresh the list
            setState((s) => (s && !s.conversations.some((c) => c.id === itemConv)
              ? { ...s, conversations: [{ id: itemConv, title: ev.item.data?.text || '', updated_at: Date.now() / 1000 }, ...s.conversations] }
              : s));
            break;
          }
          setState((s) => {
            if (!s) return s;
            const conversations = s.conversations.some((c) => c.id === itemConv)
              ? s.conversations
              : [{ id: itemConv, title: ev.item.kind === 'user' ? ev.item.data?.text || '' : '', updated_at: Date.now() / 1000 }, ...s.conversations];
            return { ...s, items: upsert(s.items, ev.item), conversations };
          });
          break;
        }
        case 'limit_reached': // Mo hit the plan's allowance: the panel opens the upgrade dialog (PlanLimits.js)
          window.dispatchEvent(new CustomEvent(PLAN_LIMIT_EVENT, { detail: { botId, artifact: ev.artifact, limit: ev.limit } }));
          break;
        case 'delta':
          setState((s) => (s ? {
            ...s,
            items: s.items.map((it) => (it.id === ev.item_id
              ? { ...it, data: { ...it.data, text: (it.data.text || '') + ev.text } } : it)),
          } : s));
          break;
        case 'run':
          setState((s) => (s ? {
            ...s,
            running: ev.running,
            run_conversation_id: ev.running ? (ev.conversation_id ?? s.run_conversation_id) : null,
            activity: ev.activity ?? s.activity,
            activity_key: ev.activity_key ?? (ev.activity != null ? null : s.activity_key),
            activity_params: ev.activity_params ?? s.activity_params,
            started_at: ev.running ? (ev.started_at ?? s.started_at) : null,
            queued: ev.queued ?? (ev.running ? s.queued : 0),
          } : s));
          break;
        case 'spec':
          if (ev.spec) h.current.onSpec?.(ev.spec);
          break;
        case 'knowledge':
          h.current.onKnowledge?.();
          break;
        case 'data':
          h.current.onData?.();
          break;
        case 'conversation_deleted':
          // deleted here or in another tab: leave it if it is open, else drop it from the list
          if (ev.conversation_id === conv.current) load(ev.current_conversation_id); // eslint-disable-line no-use-before-define
          else setState((s) => (s ? { ...s, conversations: s.conversations.filter((c) => c.id !== ev.conversation_id) } : s));
          break;
        case 'conversation_renamed': // here or in another tab
          setState((s) => (s ? {
            ...s,
            conversations: s.conversations.map((c) => (c.id === ev.conversation_id ? { ...c, title: ev.title, named: ev.named } : c)),
          } : s));
          break;
        case 'resync':
          controller?.abort();
          seq.current = ''; // reload the state, then reconnect from its stream position
          load(conv.current); // eslint-disable-line no-use-before-define
          break;
        default:
          break;
      }
    };

    const connect = async () => {
      if (closed) return;
      controller = new AbortController();
      try {
        await streamEvents(
          studioUrl(botId, `/builder/events?after=${encodeURIComponent(seq.current || '0-0')}`),
          { signal: controller.signal },
          handle,
        );
      } catch (e) {
        if (closed || e.name === 'AbortError') return;
      }
      if (!closed) timer = setTimeout(connect, 1500);
    };

    const load = async (conversationId) => {
      try {
        const q = conversationId ? `?conversation=${encodeURIComponent(conversationId)}` : '';
        const s = await studioApi.get(botId, `/builder/state${q}`);
        if (closed) return;
        conv.current = s.conversation_id || 'main';
        const firstLoad = !seq.current;
        seq.current = s.seq || '0-0';
        setState(s);
        if (firstLoad) connect();
      } catch (_) {
        if (!closed) timer = setTimeout(() => load(conversationId), 2500);
      }
    };
    reload.current = load;

    setState(null);
    seq.current = '';
    conv.current = '';
    load();
    return () => {
      closed = true;
      controller?.abort();
      if (timer) clearTimeout(timer);
    };
  }, [botId]);

  const items = state?.items || [];
  const pending = items.filter((i) => PENDING_KINDS.includes(i.kind) && i.data?.status === 'open');
  const conversationId = state?.conversation_id || 'main';

  const send = useCallback(
    (text) => studioApi.post(botId, '/builder/message', { text, conversation_id: conv.current || undefined }),
    [botId],
  );
  const openConversation = useCallback((id) => reload.current(id), []);
  const newConversation = useCallback(async () => {
    const r = await studioApi.post(botId, '/builder/conversations');
    await reload.current(r.conversation_id);
  }, [botId]);
  // one after another, the open conversation last (its fallback is then one that stays); a conversation
  // that cannot go (Mo works in it) is skipped, the first error is raised once the others are gone
  const deleteConversations = useCallback(async (ids) => {
    const open = conv.current;
    const order = [...ids.filter((id) => id !== open), ...ids.filter((id) => id === open)];
    const gone = [];
    let next = null;
    let failed = null;
    for (const id of order) {
      try {
        const r = await studioApi.del(botId, `/builder/conversations/${encodeURIComponent(id)}`); // eslint-disable-line no-await-in-loop
        gone.push(id);
        next = r.conversation_id;
      } catch (e) {
        failed = failed || e;
      }
    }
    if (gone.includes(open)) await reload.current(next);
    else setState((s) => (s ? { ...s, conversations: s.conversations.filter((c) => !gone.includes(c.id)) } : s));
    if (failed) throw failed;
  }, [botId]);
  const deleteConversation = useCallback((id) => deleteConversations([id]), [deleteConversations]);
  // the conversation list (ConversationSidebar): an own name (empty: the first message again), and the search
  const renameConversation = useCallback(async (id, title) => {
    const r = await studioApi.patch(botId, `/builder/conversations/${encodeURIComponent(id)}`, { title });
    setState((s) => (s ? {
      ...s, conversations: s.conversations.map((c) => (c.id === id ? { ...c, title: r.title, named: r.named } : c)),
    } : s));
    return r;
  }, [botId]);
  const searchConversations = useCallback(
    (q) => studioApi.get(botId, `/builder/conversations/search?q=${encodeURIComponent(q)}`).then((r) => r.conversations || []),
    [botId]);
  const answer = useCallback((itemId, answers) => studioApi.post(botId, '/builder/answer', { item_id: itemId, answers }), [botId]);
  const cancelQuestions = useCallback((itemId) => studioApi.post(botId, '/builder/answer/cancel', { item_id: itemId }), [botId]);
  const provideSecret = useCallback((itemId, value) => studioApi.post(botId, '/builder/secret', { item_id: itemId, value }), [botId]);
  const connectionDone = useCallback((itemId, connected) => studioApi.post(botId, '/builder/connection', { item_id: itemId, connected }), [botId]);
  const confirmAction = useCallback((itemId, approved) => studioApi.post(botId, '/builder/confirm', { item_id: itemId, approved }), [botId]);
  const stop = useCallback(() => studioApi.post(botId, '/builder/stop'), [botId]);

  return {
    state, items, pending, conversationId, send, openConversation, newConversation, deleteConversation,
    deleteConversations, renameConversation, searchConversations, answer, cancelQuestions, provideSecret,
    connectionDone, confirmAction, stop,
  };
}
